import {
  FormControl,
  Text,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
  Heading,
  NumberInput,
  NumberInputField,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import {
  Branch,
  CreateBranchRequest,
  branchesApi,
} from '@texas/api/endpoints/branchesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ServerError } from '@texas/types';
import { ErrorDetails } from '../../shared/alert/ErrorDetails';
import { SubmitButton } from '../../shared/form/SubmitButton';
import { TexasFormLabel } from '../../shared/form/TexasFormLabel';
import { ModalBadge } from '../../shared/modal/ModalBadge';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { FormCounter } from '@texas/components/shared/form/FormCounter';
import { useActiveSession } from '@texas/hooks/useSession';

export interface CreateBranchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBranchCreated: (branch: Branch) => void;
  identifier: string;
}

export function CreateBranchModal(props: CreateBranchModalProps) {
  const { currentUser } = useActiveSession();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateBranchRequest>({
    defaultValues: { name: '', displayOrder: 0 },
  });

  const watchName = watch('name');

  const { t } = useTranslation();
  const toast = useToast();

  const {
    request: createBranchRequest,
    loading,
    error,
  } = useApiRequest(branchesApi.createBranch);

  useEffect(() => {
    setValue('identifier', props.identifier);
  }, [props.identifier, setValue]);

  const onSubmit = async (data: CreateBranchRequest) =>
    await request(
      createBranchRequest,
      [currentUser.organizationId, data],
      (data: Branch) => {
        toast({
          title: t('general.created'),
          status: 'success',
          isClosable: true,
        });
        props.onBranchCreated(data);
      },
      (error: ServerError) => {
        toast({
          title: t('general.createFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );

  return (
    <Modal
      variant="entityDirectory"
      isOpen={props.isOpen}
      onClose={props.onClose}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalBadge>
          <Text fontWeight="bold">{props.identifier}</Text>
        </ModalBadge>
        <ModalHeader>
          <Heading size="md">{t('branch.createNew')}</Heading>
        </ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            {error && <ErrorDetails error={error} />}
            <VStack>
              <FormControl isRequired={true}>
                <TexasFormLabel>{t('branch.branchName')}</TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  {...register('name', {
                    required: true,
                    maxLength: {
                      value: 100,
                      message: t('errors.maxLength', { count: 100 }),
                    },
                  })}
                  placeholder={t('general.name')}
                />
                <FormCounter length={watchName.length} maxLength={100} />
                <ErrorLabel text={errors.name?.message} />
              </FormControl>
              <FormControl isRequired={true}>
                <TexasFormLabel>{t('branch.displayOrder')}</TexasFormLabel>
                <NumberInput>
                  <NumberInputField
                    {...register('displayOrder', {
                      required: true,
                    })}
                    placeholder="0"
                  />
                </NumberInput>
                <ErrorLabel text={errors.displayOrder?.message} />
              </FormControl>
              <FormControl>
                <TexasFormLabel>
                  {t('branch.defaultInternalSupplier')}
                </TexasFormLabel>
                <Text>{t('branch.defaultInternalSupplierNote')}</Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <SubmitButton loading={loading}>
              {t('general.create')} {props.identifier}
            </SubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
